import GuestRoute from 'src/components/guest-route/GuestRoute';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { PATHS } from './navigateRoutes';
import ProtectedRoute from 'src/components/protected-route/ProtectedRoute';
import App from 'src/App';
import LoadingIndicator from 'src/components/loader';
import CheckUpgradeRequired from 'src/components/check-upgrade-required/CheckUpgradeRequired';
import { SLASH_COMMANDS } from './slashCommands';
import FilePicker from 'src/pages/file-picker';
import NoActiveOrganization from 'src/pages/no-active-organization';
import { MCP_CONNECTOR_UI_ENABLED } from './mcpConnectors';
import { HANA_PROMPT_LIBRARY_URL } from './Urls';
import DashboardHomeRedirect from 'src/components/dashboard-home-redirect/DashboardHomeRedirect';
import SourceModuleAccess from 'src/components/source-module-access/SourceModuleAccess';

const GetStarted = lazy(() => import('src/pages/get-started'));
const Overview = lazy(() => import('src/pages/overview'));
const WhatsHappening = lazy(() => import('src/pages/whats-happening'));
const MeetingOverview = lazy(() => import('src/pages/meeting-overview'));
const ReportGroupPage = lazy(() => import('src/pages/report-group'));
const ReportMessagesList = lazy(() => import('src/pages/report-message'));
const Memory = lazy(() => import('src/pages/memory/index'));
const MemoryBatchDetails = lazy(
  () => import('src/pages/memory/memoryBatchDetails'),
);
const SearchMemoryChunks = lazy(
  () => import('src/pages/memory/searchMemoryChunks'),
);
const ViewSentPrompt = lazy(
  () => import('src/pages/sent-prompts/ViewSentPrompt'),
);
const ViewInvocation = lazy(
  () => import('src/pages/invocation-history/ViewInvocation'),
);
const OnBoardRegistration = lazy(
  () => import('src/pages/onboard-registration/OnBoardRegistration'),
);
const AuthPage = lazy(() => import('src/pages/auth'));
const ErrorPage = lazy(() => import('src/pages/error-page'));
const Members = lazy(() => import('src/pages/members'));
const Automation = lazy(() => import('src/pages/automation/Automation'));

const Help = lazy(() => import('src/pages/help'));
const Spaces = lazy(() => import('src/pages/spaces/Spaces'));
const PricingPlans = lazy(() => import('src/pages/pricing-plans'));
const Settings = lazy(() => import('src/pages/setting/Settings'));
const Instructions = lazy(() => import('src/pages/instructions/Instructions'));
const LemonsqueezyPayment = lazy(
  () => import('src/pages/lemonsqueezy-payment'),
);
const PolicyApproval = lazy(() => import('src/pages/policy-approval'));
const Crm = lazy(() => import('src/pages/crm/Crm'));
const Polls = lazy(() => import('src/pages/polls'));
const ReactivateAccount = lazy(() => import('src/pages/reactivate-account'));
const HighValueClients = lazy(() => import('src/pages/high-value-clients'));
const ManualVerifications = lazy(
  () => import('src/pages/manual-verifications'),
);
const AmlVerifications = lazy(() => import('src/pages/aml-verifications'));
const ConnectConfluence = lazy(() =>
  import('src/pages/connectors/confluence').then((m) => ({
    default: m.ConnectConfluence,
  })),
);
const ConnectNotion = lazy(() =>
  import('src/pages/connectors/notion').then((m) => ({
    default: m.ConnectNotion,
  })),
);
const ConnectJira = lazy(() =>
  import('src/pages/connectors/jira').then((m) => ({ default: m.ConnectJira })),
);
const ConnectAsana = lazy(() => import('src/pages/connectors/asana'));
const ConnectExposeApi = lazy(() =>
  import('src/pages/connectors/expose-api').then((m) => ({
    default: m.ConnectExposeApi,
  })),
);
const ConnectMcp = lazy(() => import('src/pages/connectors/mcp'));
const Tasks = lazy(() =>
  import('src/pages/tasks').then((m) => ({ default: m.Tasks })),
);
const Sprints = lazy(() =>
  import('src/pages/sprints').then((m) => ({ default: m.Sprints })),
);
const SprintBoard = lazy(() =>
  import('src/pages/sprints/SprintBoard').then((m) => ({
    default: m.SprintBoard,
  })),
);
const SprintBacklog = lazy(() =>
  import('src/pages/sprints/SprintBacklog').then((m) => ({
    default: m.SprintBacklog,
  })),
);
const SavedPrompts = lazy(() => import('src/pages/saved-prompts'));
const Tags = lazy(() => import('src/pages/tags'));
const InvocationHistoryPage = lazy(
  () => import('src/pages/invocation-history'),
);
const withSuspense = (Component: React.LazyExoticComponent<React.FC>) => (
  <Suspense fallback={<LoadingIndicator />}>
    <Component />
  </Suspense>
);

const ExternalRedirect = ({ href }: { href: string }) => {
  useEffect(() => {
    window.location.replace(href);
  }, [href]);

  return null;
};

export const ROUTES: RouteObject[] = [
  {
    path: PATHS.home,
    element: (
      <ProtectedRoute>
        <App />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        element: <DashboardHomeRedirect />,
      },
      {
        path: PATHS.getStarted,
        element: withSuspense(GetStarted),
      },
      {
        path: PATHS.overview,
        element: withSuspense(Overview),
      },
      {
        path: PATHS.whatsHappening,
        element: withSuspense(WhatsHappening),
      },
      {
        path: PATHS.meetingOverview,
        element: withSuspense(MeetingOverview),
      },
      {
        path: PATHS.users,
        element: withSuspense(Members),
      },
      {
        path: PATHS.automation,
        element: withSuspense(Automation),
      },
      {
        path: PATHS.savedPrompts,
        element: withSuspense(SavedPrompts),
      },
      {
        path: PATHS.approvals,
        element: <Navigate to={PATHS.getStarted} replace />,
      },
      {
        path: PATHS.tags,
        element: withSuspense(Tags),
      },
      {
        path: PATHS.spaces,
        element: withSuspense(Spaces),
      },
      {
        path: PATHS.connectors,
        element: <Outlet />,
        children: [
          {
            path: '',
            element: <Navigate to={PATHS.getStarted} replace />,
          },
          {
            path: PATHS.zapierConnector,
            element: <Navigate to={PATHS.getStarted} replace />,
          },
          {
            path: PATHS.exposeApiConnector,
            element: withSuspense(ConnectExposeApi),
          },
          {
            path: PATHS.confluenceConnector,
            element: withSuspense(ConnectConfluence),
          },
          {
            path: PATHS.notionConnector,
            element: withSuspense(ConnectNotion),
          },
          {
            path: PATHS.jiraConnector,
            element: withSuspense(ConnectJira),
          },
          {
            path: PATHS.asanaConnector,
            element: withSuspense(ConnectAsana),
          },
          {
            path: PATHS.mcpConnector,
            element: MCP_CONNECTOR_UI_ENABLED ? (
              withSuspense(ConnectMcp)
            ) : (
              <Navigate to={PATHS.getStarted} replace />
            ),
          },
        ],
      },
      {
        path: PATHS.settings,
        element: withSuspense(Settings),
      },
      {
        path: PATHS.instructions,
        element: withSuspense(Instructions),
      },
      {
        path: PATHS.help,
        element: withSuspense(Help),
      },
      {
        path: PATHS.tasks,
        element: withSuspense(Tasks),
      },
      {
        path: PATHS.sprints,
        element: withSuspense(Sprints),
      },
      {
        path: PATHS.sprintBoard,
        element: withSuspense(SprintBoard),
      },
      {
        path: PATHS.sprintBacklog,
        element: withSuspense(SprintBacklog),
      },
      {
        path: PATHS.reportGroups,
        element: (
          <CheckUpgradeRequired featureId={SLASH_COMMANDS.reportGroups}>
            <Outlet />
          </CheckUpgradeRequired>
        ),
        children: [
          {
            path: '',
            element: withSuspense(ReportGroupPage),
          },
          {
            path: PATHS.reportMessages,
            element: withSuspense(ReportMessagesList),
          },
        ],
      },
      {
        path: PATHS.filePicker,
        element: <FilePicker />,
      },
      {
        path: PATHS.polls,
        element: withSuspense(Polls),
      },
      {
        path: PATHS.memory,
        element: (
          <SourceModuleAccess>
            <Outlet />
          </SourceModuleAccess>
        ),
        children: [
          {
            path: '',
            element: withSuspense(Memory),
          },
          {
            path: PATHS.memoryBatchDetails,
            element: withSuspense(MemoryBatchDetails),
          },
          {
            path: PATHS.ingestedMemories,
            element: withSuspense(MemoryBatchDetails),
          },
          {
            path: PATHS.memoryPageData,
            element: withSuspense(MemoryBatchDetails),
          },
          {
            path: PATHS.memorySearch,
            element: withSuspense(SearchMemoryChunks),
          },
        ],
      },
      {
        path: PATHS.pricingPlans,
        element: withSuspense(PricingPlans),
      },
      {
        path: PATHS.payment,
        element: withSuspense(LemonsqueezyPayment),
      },
      {
        path: PATHS.crm,
        element: withSuspense(Crm),
      },
      {
        path: PATHS.highValueClients,
        element: withSuspense(HighValueClients),
      },
      {
        path: PATHS.manualVerifications,
        element: withSuspense(ManualVerifications),
      },
      {
        path: PATHS.amlVerifications,
        element: withSuspense(AmlVerifications),
      },
      {
        path: PATHS.invocationHistory,
        element: withSuspense(InvocationHistoryPage),
      },
      {
        path: PATHS.invocationPlaybook,
        element: <ExternalRedirect href={HANA_PROMPT_LIBRARY_URL} />,
      },
      {
        path: PATHS.invocationRecord,
        element: withSuspense(ViewInvocation),
      },
      {
        path: PATHS.sentPrompt,
        element: withSuspense(ViewSentPrompt),
      },
    ],
  },
  {
    path: PATHS.onBoardRegistration,
    element: (
      <ProtectedRoute>{withSuspense(OnBoardRegistration)}</ProtectedRoute>
    ),
  },
  {
    path: PATHS.policyApproval,
    element: <ProtectedRoute>{withSuspense(PolicyApproval)}</ProtectedRoute>,
  },
  {
    path: PATHS.reactivateAccount,
    element: <ProtectedRoute>{withSuspense(ReactivateAccount)}</ProtectedRoute>,
  },
  {
    path: PATHS.noActiveOrganization,
    element: (
      <ProtectedRoute>
        <NoActiveOrganization />
      </ProtectedRoute>
    ),
  },
  {
    path: PATHS.unAuthorized,
    element: <GuestRoute>{withSuspense(AuthPage)}</GuestRoute>,
  },
  {
    path: '*',
    element: withSuspense(ErrorPage),
  },
];
